import { Injectable } from '@angular/core';
import { SqliteWrapperService } from '../sqlite-wrapper/sqlite-wrapper.service';
import { SqliteObjectWrapper } from '../sqlite-wrapper/sqlite-object-wrapper';
import { AppPlatformService } from '../platform/app-platform.service';
import { runOfflineMigrations } from './offline-schema';

/**
 * La única puerta a la base offline del usuario logueado (`${dni}.db`), compartida por cursos,
 * evaluaciones, encuestas, la cola de peticiones y los checklists.
 *
 * La conexión la cachea `SqliteWrapperService`; acá solo se resuelve qué base corresponde al
 * usuario actual y se corren las migraciones una vez por base. El dni se lee en cada llamada, así
 * que un cambio de usuario abre su propia base sin reiniciar nada.
 */
@Injectable({ providedIn: 'root' })
export class OfflineDatabaseService {
  private migrations = new Map<string, Promise<number>>();

  constructor(
    private sqlite: SqliteWrapperService,
    private appPlatform: AppPlatformService
  ) {}

  /** La base del usuario actual, o `null` si no hay usuario logueado, si no es la app o no se pudo abrir. */
  async get(): Promise<SqliteObjectWrapper | null> {
    // En web no hay base local (haría falta el elemento `jeep-sqlite`): no se intenta abrir.
    if (!this.appPlatform.isAppPlatform()) return null;
    const name = this.currentDatabaseName();
    if (!name) return null;
    try {
      const db = await this.sqlite.create({ name, location: 'default' });
      await this.migrate(name, db);
      return db;
    } catch (error) {
      console.error('[OfflineDatabaseService] no se pudo abrir la base', name, error);
      return null;
    }
  }

  private migrate(name: string, db: SqliteObjectWrapper): Promise<number> {
    let done = this.migrations.get(name);
    if (!done) {
      // Si ni siquiera se pudo leer la versión, la base se entrega igual (como antes, una migración
      // fallida no bloqueaba nada) y se vuelve a intentar en la próxima apertura.
      done = runOfflineMigrations(db).catch((error) => {
        console.error('[OfflineDatabaseService] no se pudieron correr las migraciones', name, error);
        this.migrations.delete(name);
        return 0;
      });
      this.migrations.set(name, done);
    }
    return done;
  }

  private currentDatabaseName(): string | null {
    try {
      const userData = JSON.parse(localStorage.getItem('user_data'));
      return userData?.dni ? `${userData.dni}.db` : null;
    } catch {
      return null;
    }
  }
}
